import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useLocation, useNavigate } from "react-router-dom";
import IndexApp from "../../Components/IndexApp";
import { getActiveBrandConfig } from "../../config/environment.config";
import { isTeachrAccountActive } from "../../Utils/Function";
import { DispatchClientParams } from "../../config/CLIENT_TYPE_CONFIG";
import { store } from "../../Redux/store";
import { getActiveClients } from "../../Redux/Actions/StudentActions";
import { resetDeclarationV2, updateDeclarationV2 } from "../../Redux/Reducers/DeclarationV2Reducers";
import { DeclarationV2ContextType, DeclarationV2Provider } from "../../App/DeclarationV2/DeclarationV2Context";
import { DeclarationStep, DeclarationV2State, initialDeclarationV2State } from "../../App/DeclarationV2/types";
import { DV2 } from "../../App/DeclarationV2/theme";
import HomeStep from "../../App/DeclarationV2/steps/HomeStep";
import StudentStep from "../../App/DeclarationV2/steps/StudentStep";
import PlanningStep from "../../App/DeclarationV2/steps/PlanningStep";
import SessionsStep from "../../App/DeclarationV2/steps/SessionsStep";
import PricingStep from "../../App/DeclarationV2/steps/PricingStep";
import SummaryStep from "../../App/DeclarationV2/steps/SummaryStep";
import SuccessStep from "../../App/DeclarationV2/steps/SuccessStep";

/**
 * Déclaration de cours v2, route `/{marque}/tableau-de-bord/{prestation}/declarer`.
 * Nouveau parcours d'après les maquettes du 29 octobre 2026 ; l'ancien
 * assistant (`DeclarationPage`, `/declare`) n'est pas modifié.
 *
 * Arrivée sur la page : on revient toujours à l'accueil. Un parcours commencé
 * est proposé en reprise, un parcours terminé est effacé (l'audit relevait
 * l'écran de succès qui se réaffichait à la réouverture).
 */
const DeclarationV2Page = () => {
    const dispatch = useDispatch();
    const navigate = useNavigate();
    const location = useLocation();
    const teachr = useSelector((s: any) => s.user.currentUser.teachr);
    const persisted: DeclarationV2State | null = useSelector((s: any) => s.declarationV2);
    const state = persisted ?? initialDeclarationV2State();
    const activeClients = useSelector((s: any) => s?.student?.activeClients);

    const [search, setSearch] = useState("");
    const [currentPage, setCurrentPage] = useState(0);
    const [pageSize, setPageSize] = useState(10);
    const [clientsLoading, setClientsLoading] = useState(false);
    const entered = useRef(false);

    // Retour à l'accueil à chaque arrivée sur la page (voir en-tête).
    useEffect(() => {
        if (entered.current) return;
        entered.current = true;
        if (!persisted) return;
        // Arrivée depuis « Mes élèves » (Déclarer un cours) : l'élève est déjà choisi, on reste à l'étape demandée.
        if ((location.state as any)?.keepStep === true && persisted.step !== "success" && !persisted.declaredCourseId) return;
        if (persisted.step === "success" || persisted.declaredCourseId) {
            dispatch(resetDeclarationV2());
        } else if (persisted.step !== "home") {
            dispatch(updateDeclarationV2({ step: "home", resumeStep: persisted.step }));
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    // Garde : un prof dont le compte n'est pas actif ne déclare pas (le serveur refuse aussi).
    useEffect(() => {
        if (teachr?.id && !isTeachrAccountActive(teachr)) {
            navigate(`/${getActiveBrandConfig()}/profile/complete`, { replace: true });
        }
    }, [teachr, navigate]);

    const params: DispatchClientParams = useMemo(() => ({
        teachrId: teachr?.id,
        archive: 0,
        page: currentPage,
        variable: 0,
        sort: 1,
        limit: pageSize,
        validated: 1,
        search,
        onlyActive: true,
    }), [teachr?.id, currentPage, pageSize, search]);

    useEffect(() => {
        if (!teachr?.id) return;
        let cancelled = false;
        setClientsLoading(true);
        Promise.resolve(store.dispatch(getActiveClients(params) as any)).finally(() => {
            if (!cancelled) setClientsLoading(false);
        });
        return () => {
            cancelled = true;
        };
    }, [params, teachr?.id]);

    const update = useCallback((patch: Partial<DeclarationV2State>) => dispatch(updateDeclarationV2(patch)), [dispatch]);
    const reset = useCallback(() => dispatch(resetDeclarationV2()), [dispatch]);
    const goTo = useCallback((step: DeclarationStep) => {
        dispatch(updateDeclarationV2({ step }));
    }, [dispatch]);
    // Chaque étape s'ouvre en haut de page, une fois rendue : un défilement lancé avant le
    // rendu de la nouvelle étape laissait le mobile en bas de la page.
    useEffect(() => {
        if (typeof window === "undefined") return;
        const toTop = () => {
            window.scrollTo(0, 0);
            if (document.scrollingElement) document.scrollingElement.scrollTop = 0;
        };
        toTop();
        const frame = window.requestAnimationFrame(toTop);
        return () => window.cancelAnimationFrame(frame);
    }, [state.step]);

    const contextValue: DeclarationV2ContextType = useMemo(() => ({
        teachr,
        state,
        update,
        reset,
        goTo,
        clients: activeClients?.data ?? [],
        totalClients: activeClients?.totalItems ?? 0,
        currentPage,
        pageSize,
        search,
        setSearch: (value: string) => { setSearch(value); setCurrentPage(0); },
        setPage: setCurrentPage,
        setPageSize: (size: number) => { setPageSize(size); setCurrentPage(0); },
        clientsLoading,
    }), [teachr, state, update, reset, goTo, activeClients, currentPage, pageSize, search, clientsLoading]);

    const renderStep = () => {
        switch (state.step) {
            case "student": return <StudentStep />;
            case "planning": return <PlanningStep />;
            case "sessions": return <SessionsStep />;
            case "pricing": return <PricingStep />;
            case "summary": return <SummaryStep />;
            case "success": return <SuccessStep />;
            default: return <HomeStep />;
        }
    };

    return (
        <DeclarationV2Provider value={contextValue}>
            <IndexApp
                index={1}
                teachr={teachr}
                component={
                    <main style={{ background: DV2.pageBg, minHeight: "calc(100vh - 64px)" }}>
                        {renderStep()}
                    </main>
                }
            />
        </DeclarationV2Provider>
    );
};

export default DeclarationV2Page;
